import { fetchOrganizations } from 'admin/actions/organization';
import BreadCrumb from 'admin/components/molecules/BreadCrumb';
import { toQueryParams } from 'admin/utils';
import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components';
import {
  NumberParam,
  QueryParamConfig,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';
import { paths } from '~/javascripts/admin/schema/docs/api/admin';
import getQueryParamDefaultValues from '~/javascripts/admin/utils/use-query-params';
import Headings from '../../atoms/Headings';
import UserPrivateMoneyNotAllowed from '../../atoms/UserPrivateMoneyNotAllowed';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';
import MemberOrganizationFilterForm from './MemberOrganizationFilterForm';
import MemberOrganizationListTable from './MemberOrganizationListTable';

type MemberOrgs =
  paths['/api/organizations']['get']['responses']['200']['content']['application/json'];

type OrganizationsQueryParamConfig = typeof queryParamConfigs;
export type OrganizationsQueryParams = {
  [key in keyof OrganizationsQueryParamConfig]: OrganizationsQueryParamConfig[key] extends QueryParamConfig<
    unknown,
    infer T
  >
    ? T
    : never;
};

const queryParamConfigs = {
  name: withDefault(StringParam, ''),
  page: withDefault(NumberParam, 1),
};

const queryParamDefaultValues = getQueryParamDefaultValues(queryParamConfigs);

const PageWrapper = styled.div``;

const crumbs = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '決済加盟店管理', href: '', isCurrentPage: true },
  { value: '決済加盟店一覧', href: '', isCurrentPage: true },
];

const SettingsMemberOrganization = ({ match }: RouteComponentProps<{ id?: string }>) => {
  const user = useAppSelector((state) => state.user);
  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);
  const moneyId = match?.params.id ? match.params.id : userPrivateMoney.id;
  const allowedPrivateMoneys =
    user.role === 'SERVICE-MANAGER' ? user.moneys() : user.issuedMoneys();
  const isSelectedMoneyAllowed = allowedPrivateMoneys.some((m) => m.id === userPrivateMoney.id);

  const [isLoading, setIsLoading] = useState(false);
  const [memberOrgs, setMemberOrgs] = useState<MemberOrgs>({
    rows: [],
    count: 0,
    pagination: {
      current: 1,
      per_page: 30,
      has_next: false,
      has_prev: false,
      max_page: 1,
    },
  });

  const [query, setQuery] = useQueryParams({
    ...queryParamConfigs,
  });

  const clearQuery = () => {
    setQuery({ ...queryParamDefaultValues });
  };

  const fetchMembers = (queryParams: OrganizationsQueryParams) => {
    const { ...rest } = queryParams;
    setIsLoading(true);

    fetchOrganizations(
      toQueryParams(
        {
          per_page: memberOrgs?.pagination?.per_page,
          with_details: true,
          with_disabled_member_orgs: true,
          private_money_id: moneyId,
          ...rest,
        },
        true
      )
    )
      .then((members) => setMemberOrgs(members))
      .finally(() => setIsLoading(false));
  };

  const onChangePage = (page: number) => setQuery((prev) => ({ ...prev, page }));

  const refresh = () => {
    fetchMembers(query);
  };

  useEffect(() => {
    if (isSelectedMoneyAllowed) {
      fetchMembers(query);
    }
  }, [query, isSelectedMoneyAllowed]);

  return (
    <PageWrapper>
      <BreadCrumb crumbs={crumbs} />
      {isSelectedMoneyAllowed ? (
        <>
          <Headings.h6>決済加盟店検索</Headings.h6>
          <SelectedPrivateMoneyDisplay
            highlightHeader={!isSelectedMoneyAllowed}
            allowedPrivateMoneys={allowedPrivateMoneys}
          />
          <MemberOrganizationFilterForm query={query} onSearch={setQuery} onClear={clearQuery} />
          <MemberOrganizationListTable
            privateMoneyId={moneyId}
            memberOrgs={memberOrgs}
            refresh={refresh}
            isLoading={isLoading}
            pagination={memberOrgs.pagination}
            onChangePage={onChangePage}
          />
        </>
      ) : (
        <UserPrivateMoneyNotAllowed />
      )}
    </PageWrapper>
  );
};

export default SettingsMemberOrganization;
